import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Typography, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LanguageThemeBar from '../LanguageThemeBar';
import { useTranslation } from "react-i18next";
import { API_BASE } from '../../apiBase';
// 🔑 use the shared helpers so keys match the rest of the app
import { setTokens } from '../../api/api';

export default function LoginPage({ setToken }) {
  const { t } = useTranslation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA state (ISDS A2-4.1.4)
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const [maskedEmail, setMaskedEmail] = useState('');

  const navigate = useNavigate();

const handleLogin = async (e) => {
  if (e) e.preventDefault();
  setError('');
  setLoading(true);
  try {
    const res = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username.trim(),
        password: password.trim(),
      }),
    });

    let data = {};
    try {
      data = await res.json();
    } catch {
      /* ignore if no JSON */
    }

    if (res.ok && data.access_token && data.refresh_token) {
      // Direct login (no MFA)
      setTokens({ access: data.access_token, refresh: data.refresh_token });
      setToken(data.access_token); // updates App.js state, starts keep-alive
      navigate('/home');
    } else if (res.ok && data.mfa_required) {
      // MFA required - show OTP input
      console.log('MFA Response:', data);
      console.log('Masked email:', data.email);
      setMfaRequired(true);
      setMaskedEmail(data.email || 'your email');
      setError('');
    } else {
      setError(data.error || 'Login failed');
    }
  } catch {
    setError('Server error');
  } finally {
    setLoading(false);
  }
};

const handleVerifyOtp = async (e) => {
  if (e) e.preventDefault();
  setError('');
  setLoading(true);
  try {
    const res = await fetch(`${API_BASE}/login/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username.trim(),
        otp_code: otpCode.trim(),
      }),
    });

    let data = {};
    try {
      data = await res.json();
    } catch {
      /* ignore if no JSON */
    }

    if (res.ok && data.access_token && data.refresh_token) {
      // MFA successful
      setTokens({ access: data.access_token, refresh: data.refresh_token });
      setToken(data.access_token);
      navigate('/home');
    } else {
      setError(data.error || 'Invalid verification code');
      if (data.attempts_remaining !== undefined) {
        setAttemptsRemaining(data.attempts_remaining);
      }
    }
  } catch {
    setError('Server error');
  } finally {
    setLoading(false);
  }
};


  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100vh">
      <Box position="absolute" top={10} right={10}>
        <LanguageThemeBar />
      </Box>
      <Typography variant="h4" mb={2}>{t("login")}</Typography>

      {!mfaRequired ? (
        // Step 1: Username & Password
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <TextField
            label={t("username")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            autoComplete="username"
            fullWidth
            sx={{ width: 300 }}
          />
          <TextField
            label={t("password")}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            autoComplete="current-password"
            fullWidth
            sx={{ width: 300 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {error && <Typography color="error">{error}</Typography>}
          <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading}>
            {loading ? t("loading") || "Loading..." : "Login"}
          </Button>
        </form>
      ) : (
        // Step 2: OTP Verification (ISDS A2-4.1.4)
        <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body1" mb={2} textAlign="center" sx={{ maxWidth: 350 }}>
            A verification code has been sent to:
            <br />
            <strong>{maskedEmail}</strong>
            <br />
            <br />
            Please enter the 6-digit code below.
          </Typography>
          <TextField
            label="Verification Code"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
            margin="normal"
            autoComplete="one-time-code"
            fullWidth
            sx={{ width: 300 }}
            inputProps={{ maxLength: 6, pattern: '[0-9]*', inputMode: 'numeric' }}
          />
          {error && (
            <Typography color="error" sx={{ mt: 1 }}>
              {error}
              {attemptsRemaining < 5 && ` (${attemptsRemaining} attempts remaining)`}
            </Typography>
          )}
          <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading || otpCode.length !== 6}>
            {loading ? t("loading") || "Loading..." : "Verify"}
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setMfaRequired(false);
              setOtpCode('');
              setError('');
              setAttemptsRemaining(5);
            }}
            sx={{ mt: 1 }}
          >
            Back to Login
          </Button>
        </form>
      )}
    </Box>
  );
}
